/** @jsxImportSource @emotion/react */
import { useState, useEffect, Dispatch, SetStateAction, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { tabs } from 'components/layouts/Tab';
import PrjInfoPage from './regist/PrjInfoPage';
import IvesResultPage from './regist/IvesResultPage';
import { TabMenu } from 'models/md/MdIves';
import { useLocation } from 'react-router-dom';
import IvesResultAnlsPage from './regist/IvesResultAnlsPage';
import IvesInputPage from './regist/IvesInputPage';
import _ from 'lodash';
import { findMdPrjStatCd } from 'apis/md/MdProject';
import { hasRole } from 'utils/SessionUtil';
import { useCommonModal } from 'hooks/useCommonModal';
import { createTheme, ThemeProvider } from '@mui/material/styles';

export interface DefaultProps {
  elmPrjId: string;
  setElmPrjId?: Dispatch<SetStateAction<string>>;
  isEditable?: boolean;
  isRegStat?: boolean;
  ivesMdlId?: string;
  currentTabIndex?: number;
}

export interface ChildHandle {
  isDirty: () => boolean;
}

const MdIvesCmpuRegPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const state = { ...location.state };
  const { openCommonModal } = useCommonModal();
  const theme = createTheme({
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none', // 대문자 변환 방지
          },
        },
      },
    },
  });

  const childRef = useRef<ChildHandle>();
  const [locationState, setLocationState] = useState({ ...location.state });
  const [elmPrjId, setElmPrjId] = useState<string>(state.elmPrjId);
  const [ivesMdlId, setIvesMdlId] = useState<string>(state.ivesMdlId);
  const [elmPrjStatCd, setElmPrjStatCd] = useState<string>('');
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(state?.tabIdx || 0);

  const tabMenus: TabMenu[] = [
    { id: 'INFO', name: String(t('md.label.프로젝트 정보', '프로젝트 정보')) },
    { id: 'INPUT', name: String(t('md.label.형개발 투자비 Input', '형개발 투자비 Input')) },
    { id: 'RESULT', name: String(t('md.label.투자비 산출 결과', '투자비 산출 결과')) },
    { id: 'DETAIL', name: String(t('md.label.투자비 산출 결과 분석', '투자비 산출 결과 분석')) },
  ];

  const getStatus = () => {
    if (elmPrjId) {
      findMdPrjStatCd(elmPrjId).then((res) => {
        if (res.data && res.successOrNot === 'Y') {
          setElmPrjStatCd(res.data.elmPrjStatCd || '');
        }
      });
    }
  };

  useEffect(() => {
    if (location.pathname === '/md/ives-cmpu/detail') {
      setLocationState(state);

      // 파라미터 체크후 투자산출결과탭 오픈
      const searchParams = new URLSearchParams(location.search);
      const type = searchParams.get('type');
      const elmPrjIdParam = searchParams.get('elmPrjId');
      const ivesMdlIdParam = searchParams.get('ivesMdlId');

      if (type === 'REPORT' && elmPrjIdParam && ivesMdlIdParam) {
        setElmPrjId(elmPrjIdParam);
        setIvesMdlId(ivesMdlIdParam);
        setCurrentTabIndex(3);
      }
    }
  }, [location]);

  useEffect(() => {
    if (!_.isEmpty(locationState)) {
      setElmPrjId(locationState?.elmPrjId);
      setCurrentTabIndex(locationState?.tabIdx);
    }
  }, [locationState?.elmPrjId, locationState?.tabIdx]);

  useEffect(() => {
    getStatus();
  }, [elmPrjId]);

  const isEditable = useMemo(() => {
    // 프로젝트가 Drop/완료 상태이면 조회만 가능
    const statCode = ['CPLT', 'DROP'];
    if (statCode.includes(elmPrjStatCd || '')) return false;

    // 관리자 권한인 경우 수정 가능
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      return true;
    } else {
      return false;
    }
  }, [elmPrjStatCd]);

  const moveTab = (e, value) => {
    if (childRef.current && childRef.current?.isDirty()) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.msg.화면 이동시 작성중인 내용은 사라집니다. 이동하시겠습니까?',
          '화면 이동시 작성중인 내용은 사라집니다. 이동하시겠습니까?'
        ),
        yesCallback: () => {
          setCurrentTabIndex(value);
        },
      });
    } else {
      setCurrentTabIndex(value);
    }
  };

  return (
    <>
      <div css={tabs.wrap} className="mt-6 pdf-ignore">
        <ThemeProvider theme={theme}>
          <Tabs value={currentTabIndex} onChange={moveTab} css={tabs.tab}>
            {tabMenus.map((o, idx) => (
              <Tab
                key={o.id}
                label={o.name}
                id={`td-ives-cmpu-tab-${idx}`}
                aria-controls={`md-ives-cmpu-tabpanel-${idx}`}
                disabled={!elmPrjId && idx !== 0}
              />
            ))}
          </Tabs>
        </ThemeProvider>
        <span className="absolute top-0 right-0 text-transparent">{elmPrjId}</span>
      </div>
      {currentTabIndex === 0 && (
        <div className="mb-5">
          <PrjInfoPage
            ref={childRef}
            elmPrjId={elmPrjId}
            setElmPrjId={setElmPrjId}
            currentTabIndex={currentTabIndex}
            isEditable={isEditable}
            getStatus={getStatus}
          />
        </div>
      )}
      {currentTabIndex === 1 && (
        <div className="mb-5">
          <IvesInputPage
            ref={childRef}
            elmPrjId={elmPrjId}
            setElmPrjId={setElmPrjId}
            currentTabIndex={currentTabIndex}
            isEditable={isEditable}
          />
        </div>
      )}
      {currentTabIndex === 2 && (
        <div className="mb-5">
          <IvesResultPage
            ref={childRef}
            elmPrjId={elmPrjId}
            setElmPrjId={setElmPrjId}
            currentTabIndex={currentTabIndex}
            isEditable={isEditable}
          />
        </div>
      )}
      {currentTabIndex === 3 && (
        <div className="mb-5">
          <IvesResultAnlsPage
            ref={childRef}
            elmPrjId={elmPrjId}
            setElmPrjId={setElmPrjId}
            ivesMdlId={ivesMdlId}
            currentTabIndex={currentTabIndex}
            isEditable={isEditable}
          />
        </div>
      )}
    </>
  );
};

export default MdIvesCmpuRegPage;
